import { Link } from "react-router-dom";
import { ArrowLeft, Atom, ChartLineUp, Scales, Shuffle, Target, Eye, ArrowRight } from "@phosphor-icons/react";
import { SiteFooter } from "@/pages/Landing";
import Logo from "@/components/Logo";

const CONFIDENCE = [
  { l: "High Confidence", d: "Multiple corroborating sources, recent and structured data.", bg: "bg-teal-soft", tx: "text-teal", v: ">75%" },
  { l: "Medium Confidence", d: "Partial sources, proxy indicators required.", bg: "bg-blue-soft", tx: "text-blue", v: "50-75%" },
  { l: "Exploratory Insight", d: "Hypothesis-grade, requires user validation.", bg: "bg-gold-soft", tx: "text-gold", v: "<50%" },
];

const FRAMEWORK = [
  { i: Scales, t: "Bayesian Updating", d: "Prior beliefs are continuously revised as new evidence is ingested from authoritative sources." },
  { i: ChartLineUp, t: "Multi-Factor Scoring", d: "Each opportunity is scored across six institutional dimensions, each broken into weighted sub-factors." },
  { i: Target, t: "Risk Weighting", d: "Political, regulatory, currency, ESG and execution risks are normalised and aggregated into a risk-adjusted view." },
  { i: Shuffle, t: "Monte Carlo Simulations", d: "Distributional analysis of returns under thousands of macro and operational scenarios." },
  { i: Eye, t: "Scenario Analysis", d: "Base, bull and bear cases with explicit driver assumptions and sensitivities." },
];

export default function Methodology() {
  return (
    <div className="min-h-screen bg-background text-foreground" data-testid="methodology-page">
      <header className="border-b border-border bg-card">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <Logo variant="wordmark" size={32} to="/" testId="methodology-logo" />
          <Link to="/" className="text-sm text-muted-foreground hover:text-foreground inline-flex items-center gap-1.5 font-medium">
            <ArrowLeft size={14} /> Back
          </Link>
        </div>
      </header>

      <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-20">
        <div className="text-overline text-primary mb-3">Methodology</div>
        <h1 className="font-display text-3xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05]">
          A scientific approach<br /><span className="italic text-muted-foreground">to investment intelligence.</span>
        </h1>
        <p className="mt-6 text-sm sm:text-base text-muted-foreground leading-relaxed max-w-3xl">
          Orixa Invest combines institutional financial frameworks, quantitative methods and proxy intelligence models to deliver transparent, confidence-scored insights across Emerging Markets. Every conclusion is traceable, weighted and reproducible.
        </p>

        <div className="mt-14 sm:mt-16">
          <div className="text-overline text-muted-foreground mb-5">01 / Quantitative framework</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {FRAMEWORK.map((f, i) => (
              <div key={f.t} className="bg-card border border-border rounded-2xl p-6 hover:card-elev transition-shadow" data-testid={`framework-${i}`}>
                <div className="inline-flex h-10 w-10 bg-teal-soft text-teal rounded-lg items-center justify-center mb-4">
                  <f.i size={20} weight="bold" />
                </div>
                <div className="font-display text-lg tracking-tight">{f.t}</div>
                <div className="mt-2 text-sm text-muted-foreground leading-relaxed">{f.d}</div>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-14 sm:mt-20">
          <div className="text-overline text-muted-foreground mb-5">02 / Proxy intelligence models</div>
          <div className="bg-card border border-border rounded-2xl p-7 sm:p-9 card-elev">
            <p className="text-sm sm:text-base text-foreground/85 leading-relaxed">
              In Emerging Markets, direct, audited or up-to-date information is often unavailable. Rather than ignore opportunities, Orixa applies <span className="font-semibold">proxy intelligence models</span> — using correlated public indicators, comparable transactions, macroeconomic signals and alternative data to triangulate plausible estimates.
            </p>
            <p className="mt-4 text-sm sm:text-base text-foreground/85 leading-relaxed">
              Each proxy carries a transparent confidence level and a list of substituted indicators, so investment committees can independently weight or reject any input.
            </p>
            <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-3">
              {[
                { t: "Direct data unavailable", d: "Substitute with regional comparables" },
                { t: "Outdated reporting", d: "Adjust via leading indicators" },
                { t: "Asymmetric disclosure", d: "Cross-validate with alternative sources" },
              ].map((p) => (
                <div key={p.t} className="bg-secondary rounded-lg p-4">
                  <div className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground">{p.t}</div>
                  <div className="text-sm mt-1.5 font-medium">{p.d}</div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="mt-14 sm:mt-20">
          <div className="text-overline text-muted-foreground mb-5">03 / Confidence levels</div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {CONFIDENCE.map((c) => (
              <div key={c.l} className="bg-card border border-border rounded-2xl p-6">
                <div className={`inline-flex items-center gap-2 ${c.bg} ${c.tx} text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full mb-3`}>
                  <span className={`h-1.5 w-1.5 rounded-full bg-current`} /> {c.v}
                </div>
                <div className="font-display text-lg tracking-tight">{c.l}</div>
                <div className="mt-2 text-sm text-muted-foreground leading-relaxed">{c.d}</div>
              </div>
            ))}
          </div>
          <p className="mt-6 text-xs text-muted-foreground italic">Every analysis surfaces its confidence score, source list and proxy indicators where applicable.</p>
        </div>

        <div className="mt-16 sm:mt-20 bg-gradient-to-br from-primary to-[hsl(var(--info))] text-primary-foreground rounded-2xl p-7 sm:p-10 card-elev-lg">
          <div className="text-[10px] uppercase tracking-[0.18em] font-bold opacity-80 mb-3">Speak to our team</div>
          <div className="font-display text-2xl sm:text-3xl tracking-tight leading-tight">Want a methodology brief tailored to your mandate?</div>
          <a href="https://cal.com/romuald-ouattara-wttutm/call-orixa-invest" target="_blank" rel="noopener noreferrer" className="mt-6 inline-flex items-center gap-2 bg-white/15 backdrop-blur border border-white/20 rounded-lg px-5 py-3 text-sm font-semibold hover:bg-white/25 transition-colors" data-testid="methodology-book">
            Schedule a product demo <ArrowRight weight="bold" size={14} />
          </a>
        </div>
      </section>

      <SiteFooter />
    </div>
  );
}
